import React, { useState, useEffect } from 'react';
import { Home, Utensils, Users, AlertCircle, CheckCircle, ExternalLink, Download, Info, X, QrCode, Calendar, Tag, HelpCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to safely extract error message from API responses
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Plan comparison data for tooltips
const PLAN_COMPARISON = {
  accommodation_food: {
    title: 'Accommodation + Food',
    included: ['Comfortable stay at IIT Gandhinagar', 'Breakfast', 'Lunch', 'Dinner', 'Wi-Fi access', '24/7 security'],
    notIncluded: []
  },
  accommodation_only: {
    title: 'Accommodation Only',
    included: ['Comfortable stay at IIT Gandhinagar', 'Wi-Fi access', '24/7 security'],
    notIncluded: ['Breakfast', 'Lunch', 'Dinner']
  },
  food_only: {
    title: 'Food Only',
    included: ['Breakfast', 'Lunch', 'Dinner', 'Quality meals at venue'],
    notIncluded: ['Accommodation/Stay', 'Night facilities']
  }
};

// Tooltip component for plan comparison
const PlanTooltip = ({ planType, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const plan = PLAN_COMPARISON[planType];
  
  if (!plan) return children;

  return (
    <div 
      className="absolute top-3 right-3 z-10"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <div className="text-gray-400 hover:text-indigo-600 transition-colors cursor-help">
        <HelpCircle size={20} />
      </div>
      
      {isVisible && (
        <div className="absolute z-50 bottom-full right-0 mb-2 w-72 animate-fadeIn">
          <div className="bg-gray-900 text-white rounded-xl shadow-2xl p-4 text-sm">
            {/* Arrow */}
            <div className="absolute top-full right-4 border-8 border-transparent border-t-gray-900"></div>
            
            {/* Header */}
            <div className="font-bold text-base mb-3 pb-2 border-b border-gray-700 flex items-center gap-2">
              <HelpCircle size={16} className="text-blue-400" />
              What&apos;s Included?
            </div>
            
            {/* Included items */}
            <div className="mb-3">
              <p className="text-green-400 font-semibold text-xs uppercase tracking-wide mb-2">✓ Included</p>
              <ul className="space-y-1">
                {plan.included.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-200">
                    <CheckCircle size={12} className="text-green-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Not included items */}
            {plan.notIncluded.length > 0 && (
              <div>
                <p className="text-red-400 font-semibold text-xs uppercase tracking-wide mb-2">✗ Not Included</p>
                <ul className="space-y-1">
                  {plan.notIncluded.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-400">
                      <X size={12} className="text-red-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Footer hint */}
            <div className="mt-3 pt-2 border-t border-gray-700 text-xs text-gray-400 text-center">
              Hover over other plans to compare
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AccommodationBooking = ({ team }) => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bookings, setBookings] = useState([]);
  const [eligible, setEligible] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBookingType, setSelectedBookingType] = useState('');
  const [showPassModal, setShowPassModal] = useState(false);
  const [passData, setPassData] = useState(null);
  const [pricing, setPricing] = useState({
    accommodation_with_food: 2500,
    accommodation_only: 1500,
    food_only: 1000
  });
  // Per-day pricing: price per day per person
  const [datePricing, setDatePricing] = useState({
    accommodation_food: { per_day: 1000 },    // ₹1000/day/person
    accommodation_only: { per_day: 600 },     // ₹600/day/person
    food_only: { per_day: 400 }               // ₹400/day/person
  });
  // Dynamic event dates fetched from backend
  const [eventDates, setEventDates] = useState([]);
  const [maxDays, setMaxDays] = useState(3);
  const [selectedDates, setSelectedDates] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [cashfreeConfig, setCashfreeConfig] = useState(null);
  
  // State for pending booking options modal
  const [showPendingOptionsModal, setShowPendingOptionsModal] = useState(false);
  const [selectedPendingBooking, setSelectedPendingBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(false);

  const [formData, setFormData] = useState({
    member_count: 1,
    members: [{ name: '', email: '', phone: '' }]
  });

  useEffect(() => {
    fetchBookings();
    fetchPricing();
    fetchDatePricing();
    fetchCashfreeConfig();
    loadCashfreeScript();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/settings/pricing`);
      if (response.data.pricing) {
        setPricing(response.data.pricing);
      }
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const fetchDatePricing = async () => {
    try {
      const response = await axios.get(`${API}/bookings/date-pricing`);
      if (response.data.pricing) {
        setDatePricing(response.data.pricing);
      }
      // Fetch dynamic event dates
      if (response.data.event_dates_detailed) {
        setEventDates(response.data.event_dates_detailed);
      }
      if (response.data.max_days) {
        setMaxDays(response.data.max_days);
      }
    } catch (err) {
      console.error('Error fetching date pricing:', err);
    }
  };

  const fetchCashfreeConfig = async () => {
    try {
      const response = await axios.get(`${API}/cashfree/config`);
      setCashfreeConfig(response.data);
    } catch (err) {
      console.error('Error fetching Cashfree config:', err);
    }
  };

  const loadCashfreeScript = async () => {
    try {
      const { load } = await import('@cashfreepayments/cashfree-js');
      const mode = cashfreeConfig?.environment === 'PRODUCTION' ? 'production' : 'sandbox';
      const cashfreeInstance = await load({ mode });
      window._cashfreeAccomInstance = cashfreeInstance;
      return true;
    } catch (err) {
      console.error('Failed to load Cashfree SDK:', err);
      return false;
    }
  };

  const fetchBookings = async () => {
    try {
      setFetchLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bookings/my-booking`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBookings(response.data.bookings || []);
      setEligible(response.data.eligible);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setFetchLoading(false);
    }
  };

  const toggleDateSelection = (date) => {
    if (selectedDates.includes(date)) {
      setSelectedDates(selectedDates.filter(d => d !== date));
    } else if (selectedDates.length < maxDays) {
      setSelectedDates([...selectedDates, date].sort());
    }
    // Reset coupon when dates change
    setCouponApplied(null);
    setCouponCode('');
  };

  // Helper to format date range for display
  const getEventDateRangeLabel = () => {
    if (eventDates.length === 0) return 'Loading dates...';
    const firstDate = new Date(eventDates[0].date);
    const lastDate = new Date(eventDates[eventDates.length - 1].date);
    const year = firstDate.getFullYear();
    const startMonth = firstDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = firstDate.getDate();
    const endMonth = lastDate.toLocaleDateString('en-US', { month: 'short' });
    const endDay = lastDate.getDate();
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  const getBookingTypePricingKey = () => {
    if (selectedBookingType === 'accommodation_food') return 'accommodation_food';
    if (selectedBookingType === 'accommodation_only') return 'accommodation_only';
    return 'food_only';
  };

  const calculatePrice = () => {
    const numDays = selectedDates.length;
    if (numDays === 0) return 0;
    
    const pricingKey = getBookingTypePricingKey();
    // Get per-day price and calculate: per_day × num_days × num_members
    const perDayPrice = datePricing[pricingKey]?.per_day || 0;
    
    return perDayPrice * numDays * formData.member_count;
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setApplyingCoupon(true);
    try {
      const originalAmount = calculatePrice();
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/coupons/apply`, {
        code: couponCode.toUpperCase(),
        fee_type: selectedBookingType,
        original_amount: originalAmount
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        setError('');
      }
    } catch (err) {
      console.error('Coupon apply error:', err);
      setError(getErrorMessage(err, 'Invalid coupon code'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...formData.members];
    newMembers[index][field] = value;
    setFormData({ ...formData, members: newMembers });
  };

  const MAX_MEMBERS = 10; // Maximum allowed members for booking

  const handleMemberCountChange = (count) => {
    const newCount = parseInt(count) || 1;
    
    // Validate maximum 10 members
    if (newCount > MAX_MEMBERS) {
      setError(`Maximum ${MAX_MEMBERS} members allowed per booking`);
      return;
    }
    
    // Also check against team size
    if (newCount > team.total_members) {
      setError(`Member count cannot exceed team size (${team.total_members})`);
      return;
    }
    
    const newMembers = [...formData.members];
    while (newMembers.length < newCount) {
      newMembers.push({ name: '', email: '', phone: '' });
    }
    while (newMembers.length > newCount) {
      newMembers.pop();
    }
    
    setFormData({ member_count: newCount, members: newMembers });
    setError('');
  };

  const prefillFromTeam = () => {
    // Prefill members from team data (limited to max allowed)
    const maxPrefill = Math.min(formData.member_count, team.members.length, MAX_MEMBERS);
    const teamMembers = team.members.slice(0, maxPrefill).map(m => ({
      name: m.name,
      email: m.email,
      phone: m.phone
    }));
    
    while (teamMembers.length < formData.member_count) {
      teamMembers.push({ name: '', email: '', phone: '' });
    }
    
    setFormData({ ...formData, members: teamMembers });
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validate phone format (at least 10 digits)
  const isValidPhone = (phone) => {
    return /^\+?[\d\s-]{10,}$/.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate date selection
    if (selectedDates.length === 0) {
      setError('Please select at least 1 day');
      setLoading(false);
      return;
    }

    // Comprehensive validation
    const validationErrors = [];
    
    // Check member count
    if (formData.member_count < 1) {
      validationErrors.push('At least 1 member is required');
    }
    if (formData.member_count > MAX_MEMBERS) {
      validationErrors.push(`Maximum ${MAX_MEMBERS} members allowed`);
    }

    // Check each member's details
    formData.members.forEach((member, index) => {
      if (!member.name || member.name.trim().length < 2) {
        validationErrors.push(`Member ${index + 1}: Name is required (min 2 characters)`);
      }
      if (!member.email) {
        validationErrors.push(`Member ${index + 1}: Email is required`);
      } else if (!isValidEmail(member.email)) {
        validationErrors.push(`Member ${index + 1}: Invalid email format`);
      }
      if (!member.phone) {
        validationErrors.push(`Member ${index + 1}: Phone is required`);
      } else if (!isValidPhone(member.phone)) {
        validationErrors.push(`Member ${index + 1}: Invalid phone number (min 10 digits)`);
      }
    });

    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      setLoading(false);
      return;
    }

    try {
      // Load Cashfree script
      const scriptLoaded = await loadCashfreeScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      const token = localStorage.getItem('token');
      const authHeaders = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Create date-based booking
      const response = await axios.post(`${API}/bookings/date-based`, {
        booking_type: selectedBookingType,
        selected_dates: selectedDates,
        member_count: formData.member_count,
        members: formData.members,
        coupon_code: couponApplied ? couponApplied.code : null
      }, authHeaders);

      const { booking, cashfree_order } = response.data;

      if (cashfree_order && cashfree_order.payment_session_id) {
        // Initialize Cashfree SDK
        const cashfree = window._cashfreeAccomInstance;

        // Open Cashfree checkout
        const checkoutOptions = {
          paymentSessionId: cashfree_order.payment_session_id,
          redirectTarget: '_modal'
        };

        cashfree.checkout(checkoutOptions).then(async (result) => {
          console.log('Cashfree checkout result:', result);
          
          // Defensive cleanup: Remove Cashfree modal container if it exists
          const cleanupCashfreeModal = () => {
            const modalContainer = document.getElementById('cashfree-modal-container');
            if (modalContainer) {
              modalContainer.remove();
            }
          };
          
          // Handle user cancellation or closing the payment modal
          if (!result || (!result.error && !result.paymentDetails && !result.redirect)) {
            console.log('Payment cancelled or closed by user');
            cleanupCashfreeModal();
            setLoading(false);
            setSuccess('Payment cancelled. You can retry payment from the bookings list.');
            setShowBookingForm(false);
            fetchBookings();
            return;
          }
          
          if (result.error) {
            cleanupCashfreeModal();
            setError('Payment failed: ' + (result.error.message || 'Unknown error'));
            setLoading(false);
            return;
          }

          if (result.paymentDetails) {
            cleanupCashfreeModal();
            try {
              // Verify payment
              await axios.post(`${API}/cashfree/verify-payment`, {
                order_id: cashfree_order.order_id,
                payment_type: 'accommodation_booking',
                booking_id: booking.id
              }, authHeaders);

              // Redeem coupon if applied
              if (couponApplied) {
                try {
                  await axios.post(`${API}/coupons/redeem`, null, {
                    params: {
                      code: couponApplied.code,
                      fee_type: selectedBookingType,
                      amount_paid: booking.total_amount
                    },
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                } catch (err) {
                  console.error('Failed to redeem coupon:', err);
                }
              }

              setSuccess('Payment successful! Your booking is confirmed.');
              setShowBookingForm(false);
              setSelectedDates([]);
              setCouponApplied(null);
              setCouponCode('');
              fetchBookings();
            } catch (err) {
              setError('Payment verification failed. Please contact support.');
            }
          }
          
          if (result.redirect) {
            // Payment is being processed via redirect
            cleanupCashfreeModal();
            setLoading(false);
            return;
          }
          
          cleanupCashfreeModal();
          setLoading(false);
        }).catch((err) => {
          console.error('Cashfree checkout error:', err);
          // Cleanup on error
          const modalContainer = document.getElementById('cashfree-modal-container');
          if (modalContainer) {
            modalContainer.remove();
          }
          setError('Payment failed: ' + (err.message || 'Unknown error'));
          setLoading(false);
        });
      } else {
        setSuccess('Booking created! Please complete payment.');
        setShowBookingForm(false);
        fetchBookings();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create booking'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPass = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bookings/download/${bookingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPassData(response.data);
      setShowPassModal(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to download pass'));
    }
  };

  const startBooking = (type) => {
    setSelectedBookingType(type);
    setFormData({
      member_count: 1,
      members: [{ name: '', email: '', phone: '' }]
    });
    setShowBookingForm(true);
    setError('');
    setSuccess('');
  };

  // Show options modal for a pending booking
  const showPendingBookingOptions = (booking) => {
    setSelectedPendingBooking(booking);
    setShowPendingOptionsModal(true);
  };

  // Cancel a pending booking and optionally start a new one
  const cancelPendingBooking = async (booking, startNewBooking = false) => {
    setCancellingBooking(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bookings/cancel/${booking.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSuccess(`Pending booking ${booking.booking_id} cancelled successfully`);
      setShowPendingOptionsModal(false);
      setSelectedPendingBooking(null);
      
      // Refresh bookings list
      await fetchBookings();
      
      // If user wants to start a new booking, open the booking form
      if (startNewBooking) {
        setTimeout(() => {
          startBooking(booking.booking_type);
        }, 500);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to cancel booking'));
    } finally {
      setCancellingBooking(false);
    }
  };

  // Retry payment for a pending booking
  const retryPayment = async (booking) => {
    setLoading(true);
    setError('');
    
    try {
      // Load Cashfree script
      const scriptLoaded = await loadCashfreeScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      const token = localStorage.getItem('token');
      const authHeaders = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Call retry payment endpoint
      const response = await axios.post(`${API}/bookings/retry-payment/${booking.id}`, {}, authHeaders);
      
      const { cashfree_order } = response.data;

      if (cashfree_order && cashfree_order.payment_session_id) {
        // Initialize Cashfree SDK
        const cashfree = window._cashfreeAccomInstance;

        // Open Cashfree checkout
        const checkoutOptions = {
          paymentSessionId: cashfree_order.payment_session_id,
          redirectTarget: '_modal'
        };

        cashfree.checkout(checkoutOptions).then(async (result) => {
          // Defensive cleanup: Remove Cashfree modal container if it exists
          const cleanupCashfreeModal = () => {
            const modalContainer = document.getElementById('cashfree-modal-container');
            if (modalContainer) {
              modalContainer.remove();
            }
          };
          
          // Handle user cancellation
          if (!result || (!result.error && !result.paymentDetails && !result.redirect)) {
            console.log('Payment cancelled or closed by user');
            cleanupCashfreeModal();
            setLoading(false);
            setSuccess('Payment cancelled. You can retry payment anytime.');
            return;
          }
          
          if (result.error) {
            cleanupCashfreeModal();
            setError(`Payment failed: ${result.error.message || 'Unknown error'}`);
            setLoading(false);
            return;
          }

          if (result.paymentDetails) {
            cleanupCashfreeModal();
            try {
              // Verify payment
              await axios.post(`${API}/cashfree/verify-payment`, {
                order_id: cashfree_order.order_id,
                payment_type: 'accommodation_booking',
                booking_id: booking.id
              }, authHeaders);

              setSuccess('Payment successful! Your booking is confirmed.');
              fetchBookings();
            } catch (err) {
              console.error('Payment verification failed:', err);
              setError('Payment verification failed. Please contact support.');
            }
          }
          
          cleanupCashfreeModal();
          setLoading(false);
        }).catch((err) => {
          // Cleanup on error
          const modalContainer = document.getElementById('cashfree-modal-container');
          if (modalContainer) {
            modalContainer.remove();
          }
          setError(`Payment failed: ${err.message || 'Unknown error'}`);
          setLoading(false);
        });
      }
    } catch (err) {
      console.error('Retry payment error:', err);
      setError(getErrorMessage(err, 'Failed to initiate payment. Please try again.'));
      setLoading(false);
    }
  };

  // Only consider COMPLETED bookings as "already booked" - pending bookings can be retried
  const hasAccommodationBooking = bookings.some(b => b.booking_type === 'accommodation_food' && b.payment_status === 'completed');
  const hasAccommodationOnlyBooking = bookings.some(b => b.booking_type === 'accommodation_only' && b.payment_status === 'completed');
  const hasFoodOnlyBooking = bookings.some(b => b.booking_type === 'food_only' && b.payment_status === 'completed');
  
  // Check for pending bookings to allow retry
  const pendingAccommodationBooking = bookings.find(b => b.booking_type === 'accommodation_food' && b.payment_status === 'pending');
  const pendingAccommodationOnlyBooking = bookings.find(b => b.booking_type === 'accommodation_only' && b.payment_status === 'pending');
  const pendingFoodOnlyBooking = bookings.find(b => b.booking_type === 'food_only' && b.payment_status === 'pending');

  if (fetchLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border-2 border-purple-200 mb-8">
        <div className="flex items-center justify-center py-8">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!eligible) {
    return null; // Don't show if not eligible
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-6 md:p-8 border-2 border-indigo-200 mb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-indigo-600 p-3 rounded-full">
          <Home className="text-white" size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Accommodation & Food Booking</h2>
          <p className="text-sm text-gray-600">Book accommodation and food passes for the event</p>
        </div>
      </div>

      {/* Info Note */}
      <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-blue-800 font-semibold mb-1">Flexible Date-Based Booking</p>
          <p className="text-blue-700 text-sm">
            Choose from <span className="font-bold">three booking options</span>: Accommodation + Food, Accommodation Only, or Food Only.
            Select 1, 2, or 3 days based on your needs. Apply coupon codes for discounts during checkout!
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Existing Bookings */}
      {bookings.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Your Bookings</h3>
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div 
                key={booking.booking_id} 
                className={`p-4 rounded-lg border-2 ${
                  booking.payment_status === 'completed' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {booking.booking_type === 'accommodation_food' ? (
                        <Home className="text-indigo-600" size={20} />
                      ) : booking.booking_type === 'accommodation_only' ? (
                        <Home className="text-blue-600" size={20} />
                      ) : (
                        <Utensils className="text-orange-600" size={20} />
                      )}
                      <span className="font-bold text-gray-800">
                        {booking.booking_type === 'accommodation_food' 
                          ? 'Accommodation + Food Pass' 
                          : booking.booking_type === 'accommodation_only'
                            ? 'Accommodation Only Pass'
                            : 'Food Only Pass'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Booking ID: <span className="font-mono font-semibold">{booking.booking_id}</span></p>
                    <p className="text-sm text-gray-600">Members: {booking.member_count} | Days: {booking.num_days || booking.validity_days}</p>
                    <p className="text-sm text-gray-600">Amount: <span className="font-semibold">₹{booking.total_amount?.toLocaleString()}</span></p>
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                      booking.payment_status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {booking.payment_status === 'completed' ? '✓ Payment Confirmed' : '⏳ Payment Pending'}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {booking.payment_status === 'completed' ? (
                      <button
                        onClick={() => downloadPass(booking.booking_id)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 flex items-center gap-2 text-sm"
                      >
                        <Download size={16} />
                        Download Pass
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => retryPayment(booking)}
                          disabled={loading}
                          data-testid={`retry-payment-${booking.booking_type}`}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              Complete Payment
                              <ExternalLink size={16} />
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => showPendingBookingOptions(booking)}
                          disabled={loading || cancellingBooking}
                          data-testid={`options-${booking.booking_type}`}
                          className="bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                        >
                          <X size={16} />
                          Cancel & Rebook
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Options */}
      {!showBookingForm && (
        <div className="grid md:grid-cols-3 gap-6">
          {/* Accommodation + Food */}
          <div className={`bg-white rounded-xl p-6 border-2 ${hasAccommodationBooking ? 'border-gray-200 opacity-60' : 'border-indigo-200 hover:border-indigo-400'} transition-all relative`}>
            {/* Compare Plans Tooltip Trigger */}
            <PlanTooltip planType="accommodation_food" />
            
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-100 p-3 rounded-full">
                <Home className="text-indigo-600" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Accommodation + Food</h3>
                <p className="text-sm text-gray-600">Stay + All Meals</p>
                <p className="text-lg font-bold text-indigo-600 mt-1">₹{datePricing.accommodation_food?.per_day || 1000}/day/person</p>
              </div>
            </div>
            <ul className="text-sm text-gray-600 mb-4 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Comfortable accommodation at IIT Gandhinagar
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Breakfast, lunch & dinner included
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Select 1, 2, or 3 days
              </li>
            </ul>
            {pendingAccommodationBooking ? (
              <button
                onClick={() => retryPayment(pendingAccommodationBooking)}
                disabled={loading}
                data-testid="retry-accommodation-food-btn"
                className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    Retry Payment
                    <ExternalLink size={18} />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => startBooking('accommodation_food')}
                disabled={hasAccommodationBooking}
                data-testid="book-accommodation-food-btn"
                className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  hasAccommodationBooking 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {hasAccommodationBooking ? 'Already Booked' : 'Book Now'}
                {!hasAccommodationBooking && <ExternalLink size={18} />}
              </button>
            )}
          </div>

          {/* Accommodation Only */}
          <div className={`bg-white rounded-xl p-6 border-2 ${hasAccommodationOnlyBooking ? 'border-gray-200 opacity-60' : 'border-blue-200 hover:border-blue-400'} transition-all relative`}>
            {/* Compare Plans Tooltip Trigger */}
            <PlanTooltip planType="accommodation_only" />
            
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Home className="text-blue-600" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Accommodation Only</h3>
                <p className="text-sm text-gray-600">Stay Only (No Meals)</p>
                <p className="text-lg font-bold text-blue-600 mt-1">₹{datePricing.accommodation_only?.per_day || 600}/day/person</p>
              </div>
            </div>
            <ul className="text-sm text-gray-600 mb-4 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Comfortable accommodation at IIT Gandhinagar
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Self-arranged meals
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Select 1, 2, or 3 days
              </li>
            </ul>
            {pendingAccommodationOnlyBooking ? (
              <button
                onClick={() => retryPayment(pendingAccommodationOnlyBooking)}
                disabled={loading}
                data-testid="retry-accommodation-only-btn"
                className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    Retry Payment
                    <ExternalLink size={18} />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => startBooking('accommodation_only')}
                disabled={hasAccommodationOnlyBooking}
                data-testid="book-accommodation-only-btn"
                className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  hasAccommodationOnlyBooking 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {hasAccommodationOnlyBooking ? 'Already Booked' : 'Book Now'}
                {!hasAccommodationOnlyBooking && <ExternalLink size={18} />}
              </button>
            )}
          </div>

          {/* Food Only */}
          <div className={`bg-white rounded-xl p-6 border-2 ${hasFoodOnlyBooking ? 'border-gray-200 opacity-60' : 'border-orange-200 hover:border-orange-400'} transition-all relative`}>
            {/* Compare Plans Tooltip Trigger */}
            <PlanTooltip planType="food_only" />
            
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-3 rounded-full">
                <Utensils className="text-orange-600" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Food Only Pass</h3>
                <p className="text-sm text-gray-600">All Meals (No Stay)</p>
                <p className="text-lg font-bold text-orange-600 mt-1">₹{datePricing.food_only?.per_day || 400}/day/person</p>
              </div>
            </div>
            <ul className="text-sm text-gray-600 mb-4 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Breakfast, lunch & dinner included
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Quality meals at the event venue
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Select 1, 2, or 3 days
              </li>
            </ul>
            {pendingFoodOnlyBooking ? (
              <button
                onClick={() => retryPayment(pendingFoodOnlyBooking)}
                disabled={loading}
                data-testid="retry-food-only-btn"
                className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    Retry Payment
                    <ExternalLink size={18} />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={() => startBooking('food_only')}
                disabled={hasFoodOnlyBooking}
                data-testid="book-food-only-btn"
                className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  hasFoodOnlyBooking 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {hasFoodOnlyBooking ? 'Already Booked' : 'Book Now'}
                {!hasFoodOnlyBooking && <ExternalLink size={18} />}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Booking Form */}
      {showBookingForm && (
        <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                {selectedBookingType === 'accommodation_food' 
                  ? 'Book Accommodation + Food Pass' 
                  : selectedBookingType === 'accommodation_only'
                    ? 'Book Accommodation Only Pass'
                    : 'Book Food Only Pass'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Complete all member details to proceed with booking
              </p>
            </div>
            <button
              onClick={() => setShowBookingForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          {/* Validation Info Box */}
          <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Booking Requirements:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Maximum <strong>{MAX_MEMBERS} members</strong> allowed per booking</li>
                  <li>All member details (Name, Email, Phone) are <strong>mandatory</strong></li>
                  <li>Valid email and phone number required for each member</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date Selection */}
            <div className="p-6 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-teal-600">
                  <Calendar className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800">Select Dates ({getEventDateRangeLabel()})</h4>
                  <p className="text-sm text-gray-600">Choose which days you need this service</p>
                </div>
              </div>

              {eventDates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  Loading event dates...
                </div>
              ) : (
                <div className={`grid gap-3 ${eventDates.length <= 3 ? 'grid-cols-3' : eventDates.length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
                  {eventDates.map((dateInfo) => (
                    <button
                      key={dateInfo.date}
                      type="button"
                      onClick={() => toggleDateSelection(dateInfo.date)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedDates.includes(dateInfo.date)
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-teal-400'
                      }`}
                    >
                      <p className="font-bold text-lg">{dateInfo.label}</p>
                      <p className={`text-sm ${selectedDates.includes(dateInfo.date) ? 'text-teal-200' : 'text-gray-500'}`}>
                        {dateInfo.day}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between bg-white rounded-lg p-3">
                <span className="text-gray-600">Selected: <strong>{selectedDates.length} day(s)</strong></span>
                <span className="font-bold text-teal-700">
                  ₹{(() => {
                    const numDays = selectedDates.length;
                    if (numDays === 0) return 0;
                    const pricingKey = getBookingTypePricingKey();
                    // Calculate: per_day × num_days for per-person display
                    const perDayPrice = datePricing[pricingKey]?.per_day || 0;
                    return perDayPrice * numDays;
                  })()} /person
                </span>
              </div>
            </div>

            {/* Member Count Selection - Enhanced UI */}
            <div className={`p-6 rounded-xl border-3 ${
              selectedBookingType === 'accommodation_food' 
                ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-400' 
                : selectedBookingType === 'accommodation_only'
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400'
                  : 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-400'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-full ${
                  selectedBookingType === 'accommodation_food' 
                    ? 'bg-indigo-600' 
                    : selectedBookingType === 'accommodation_only'
                      ? 'bg-blue-600'
                      : 'bg-orange-600'
                }`}>
                  <Users className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800">
                    How many members need {selectedBookingType === 'accommodation_food' ? 'Accommodation + Food' : selectedBookingType === 'accommodation_only' ? 'Accommodation Only' : 'Food Only'}?
                  </h4>
                  <p className="text-sm text-gray-600">Select the number of team members for this booking</p>
                </div>
              </div>

              {/* Prominent Number Selector */}
              <div className="bg-white rounded-xl p-5 border-2 border-dashed border-gray-300 shadow-inner">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  {/* Number Input with +/- Buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleMemberCountChange(formData.member_count - 1)}
                      disabled={formData.member_count <= 1}
                      className={`w-12 h-12 rounded-full font-bold text-xl flex items-center justify-center transition-all ${
                        formData.member_count <= 1 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : selectedBookingType === 'accommodation_food'
                            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:scale-110'
                            : selectedBookingType === 'accommodation_only'
                              ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:scale-110'
                              : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:scale-110'
                      }`}
                    >
                      −
                    </button>
                    
                    <div className={`relative w-28 h-20 flex items-center justify-center rounded-xl border-3 ${
                      selectedBookingType === 'accommodation_food' 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : selectedBookingType === 'accommodation_only'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-orange-500 bg-orange-50'
                    }`}>
                      <input
                        type="number"
                        min="1"
                        max={Math.min(team.total_members, MAX_MEMBERS)}
                        value={formData.member_count}
                        onChange={(e) => handleMemberCountChange(e.target.value)}
                        className={`w-full h-full text-center text-4xl font-bold bg-transparent focus:outline-none ${
                          selectedBookingType === 'accommodation_food' 
                            ? 'text-indigo-700' 
                            : 'text-orange-700'
                        }`}
                      />
                      <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white px-2 text-xs text-gray-500 font-medium">
                        MEMBERS
                      </span>
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => handleMemberCountChange(formData.member_count + 1)}
                      disabled={formData.member_count >= Math.min(team.total_members, MAX_MEMBERS)}
                      className={`w-12 h-12 rounded-full font-bold text-xl flex items-center justify-center transition-all ${
                        formData.member_count >= Math.min(team.total_members, MAX_MEMBERS)
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : selectedBookingType === 'accommodation_food'
                            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:scale-110'
                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:scale-110'
                      }`}
                    >
                      +
                    </button>
                  </div>

                  {/* Quick Select Buttons */}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-2 font-medium">QUICK SELECT:</p>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5].filter(n => n <= Math.min(team.total_members, MAX_MEMBERS)).map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => handleMemberCountChange(num)}
                          className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all ${
                            formData.member_count === num
                              ? selectedBookingType === 'accommodation_food'
                                ? 'bg-indigo-600 text-white shadow-lg scale-110'
                                : 'bg-orange-600 text-white shadow-lg scale-110'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                      {Math.min(team.total_members, MAX_MEMBERS) > 5 && (
                        <button
                          type="button"
                          onClick={() => handleMemberCountChange(Math.min(team.total_members, MAX_MEMBERS))}
                          className={`px-3 h-10 rounded-lg font-semibold text-sm transition-all ${
                            formData.member_count === Math.min(team.total_members, MAX_MEMBERS)
                              ? selectedBookingType === 'accommodation_food'
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'bg-orange-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Max ({Math.min(team.total_members, MAX_MEMBERS)})
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Prefill Button */}
                  <button
                    type="button"
                    onClick={prefillFromTeam}
                    className={`px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all ${
                      selectedBookingType === 'accommodation_food'
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    <Users size={16} />
                    Auto-fill from Team
                  </button>
                </div>

                {/* Capacity Info */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      <strong className="text-gray-700">Your Team Size:</strong> {team.total_members} members
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500">
                      <strong className="text-gray-700">Max Allowed:</strong> {MAX_MEMBERS} per booking
                    </span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    selectedBookingType === 'accommodation_food'
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {formData.member_count} of {Math.min(team.total_members, MAX_MEMBERS)} selected
                  </span>
                </div>
              </div>
            </div>

            {/* Member Details */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Member Details</h4>
              <p className="text-sm text-gray-500 mb-4">All fields marked with <span className="text-red-500">*</span> are mandatory</p>
              {formData.members.map((member, index) => (
                <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-semibold text-gray-700">Member {index + 1}</h5>
                    {member.name && member.email && member.phone && isValidEmail(member.email) && isValidPhone(member.phone) ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle size={12} /> Complete
                      </span>
                    ) : (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <AlertCircle size={12} /> Incomplete
                      </span>
                    )}
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                        placeholder="Enter full name"
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-indigo-200 ${
                          member.name && member.name.trim().length >= 2 
                            ? 'border-green-300 focus:border-green-500' 
                            : 'border-gray-300 focus:border-indigo-500'
                        }`}
                        required
                        minLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={member.email}
                        onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                        placeholder="name@example.com"
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-indigo-200 ${
                          member.email && isValidEmail(member.email)
                            ? 'border-green-300 focus:border-green-500' 
                            : 'border-gray-300 focus:border-indigo-500'
                        }`}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={member.phone}
                        onChange={(e) => handleMemberChange(index, 'phone', e.target.value)}
                        placeholder="+91 XXXXX XXXXX"
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-indigo-200 ${
                          member.phone && isValidPhone(member.phone)
                            ? 'border-green-300 focus:border-green-500' 
                            : 'border-gray-300 focus:border-indigo-500'
                        }`}
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Coupon/Referral Code Section */}
            <div className="p-5 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-green-600">
                  <Tag className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-800">Have a Coupon or Referral Code?</h4>
                  <p className="text-sm text-gray-600">Apply your discount code to get special offers</p>
                </div>
              </div>

              {couponApplied ? (
                <div className="bg-white rounded-lg p-4 border-2 border-green-400">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-600" size={24} />
                      <div>
                        <p className="font-bold text-green-700">{couponApplied.code}</p>
                        <p className="text-sm text-green-600">
                          {couponApplied.discount_type === 'percentage' 
                            ? `${couponApplied.discount_value}% discount applied`
                            : `₹${couponApplied.discount_value} discount applied`}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCouponApplied(null);
                        setCouponCode('');
                      }}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon or referral code"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 font-mono text-lg uppercase"
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon || !couponCode.trim()}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="apply-coupon-btn"
                  >
                    {applyingCoupon ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Applying...
                      </>
                    ) : (
                      'Apply'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Booking Summary with Price Calculation */}
            <div className={`rounded-xl p-5 border-2 ${
              selectedBookingType === 'accommodation_food'
                ? 'bg-indigo-50 border-indigo-300'
                : selectedBookingType === 'accommodation_only'
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-orange-50 border-orange-300'
            }`}>
              <h4 className={`font-bold text-lg mb-4 ${
                selectedBookingType === 'accommodation_food'
                  ? 'text-indigo-800'
                  : selectedBookingType === 'accommodation_only'
                    ? 'text-blue-800'
                    : 'text-orange-800'
              }`}>
                Booking Summary
              </h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-600">Team</p>
                  <p className="font-semibold text-gray-800">{team.team_name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Pass Type</p>
                  <p className="font-semibold text-gray-800">
                    {selectedBookingType === 'accommodation_food' 
                      ? 'Accommodation + Food' 
                      : selectedBookingType === 'accommodation_only'
                        ? 'Accommodation Only'
                        : 'Food Only'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Members</p>
                  <p className="font-semibold text-gray-800">{formData.member_count}</p>
                </div>
                <div>
                  <p className="text-gray-600">Days Selected</p>
                  <p className="font-semibold text-gray-800">{selectedDates.length} Day(s)</p>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-500 text-sm">
                    <span>Per Day Rate</span>
                    <span>₹{datePricing[getBookingTypePricingKey()]?.per_day || 0}/day/person</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Base Price (₹{datePricing[getBookingTypePricingKey()]?.per_day || 0} × {selectedDates.length} days × {formData.member_count} members)</span>
                    <span className={couponApplied ? 'line-through' : ''}>₹{calculatePrice().toLocaleString()}</span>
                  </div>
                  
                  {couponApplied && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({couponApplied.code})</span>
                      <span>- ₹{couponApplied.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  
                  <hr className="my-2" />
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Amount</span>
                    <span className={`${
                      selectedBookingType === 'accommodation_food'
                        ? 'text-indigo-700'
                        : selectedBookingType === 'accommodation_only'
                          ? 'text-blue-700'
                          : 'text-orange-700'
                    }`}>
                      ₹{(couponApplied ? couponApplied.final_amount : calculatePrice()).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading || selectedDates.length === 0}
                data-testid="proceed-to-payment-btn"
                className={`flex-1 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 ${
                  selectedBookingType === 'accommodation_food'
                    ? 'bg-indigo-600 hover:bg-indigo-700'
                    : selectedBookingType === 'accommodation_only'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-orange-600 hover:bg-orange-700'
                } text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    Proceed to Payment - ₹{(couponApplied ? couponApplied.final_amount : calculatePrice()).toLocaleString()}
                    <ExternalLink size={20} />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="px-6 py-4 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pass Modal */}
      {showPassModal && passData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Booking Confirmation Pass</h2>
                <button onClick={() => setShowPassModal(false)} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200">
                  <img 
                    src={`data:image/png;base64,${passData.qr_code}`} 
                    alt="Booking QR Code"
                    className="w-40 h-40"
                  />
                </div>
              </div>

              {/* Booking Details */}
              <div className="space-y-4">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Booking ID</p>
                  <p className="text-xl font-bold font-mono text-indigo-600">{passData.booking.booking_id}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Team Name</p>
                    <p className="font-semibold text-gray-800">{passData.booking.team_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pass Type</p>
                    <p className="font-semibold text-gray-800">
                      {passData.booking.booking_type === 'accommodation_food' 
                        ? 'Accommodation + Food' 
                        : passData.booking.booking_type === 'accommodation_only'
                          ? 'Accommodation Only'
                          : 'Food Only'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Team Leader</p>
                    <p className="font-semibold text-gray-800">{passData.booking.leader_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Validity</p>
                    <p className="font-semibold text-gray-800">{passData.booking.validity_days} Days</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Members ({passData.booking.member_count})</p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {passData.booking.members.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-2 py-1">
                        <Users size={14} className="text-gray-500" />
                        <span className="text-sm text-gray-800">{member.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
                  <p className="text-green-800 font-bold">Payment Confirmed</p>
                  <p className="text-green-700 text-sm">This pass is valid for entry</p>
                </div>
              </div>

              <button
                onClick={() => window.print()}
                className="w-full mt-6 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 flex items-center justify-center gap-2"
              >
                <Download size={18} />
                Print / Save Pass
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Booking Options Modal */}
      {showPendingOptionsModal && selectedPendingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Pending Booking Options</h2>
                <button 
                  onClick={() => {
                    setShowPendingOptionsModal(false);
                    setSelectedPendingBooking(null);
                  }} 
                  className="text-white hover:text-gray-200"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Booking Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  {selectedPendingBooking.booking_type === 'accommodation_food' ? (
                    <Home className="text-indigo-600" size={20} />
                  ) : selectedPendingBooking.booking_type === 'accommodation_only' ? (
                    <Home className="text-blue-600" size={20} />
                  ) : (
                    <Utensils className="text-orange-600" size={20} />
                  )}
                  <span className="font-bold text-gray-800">
                    {selectedPendingBooking.booking_type === 'accommodation_food' 
                      ? 'Accommodation + Food' 
                      : selectedPendingBooking.booking_type === 'accommodation_only'
                        ? 'Accommodation Only'
                        : 'Food Only'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">Booking ID: <span className="font-mono">{selectedPendingBooking.booking_id}</span></p>
                <p className="text-sm text-gray-600">Amount: <span className="font-semibold">₹{selectedPendingBooking.total_amount?.toLocaleString()}</span></p>
                <p className="text-sm text-gray-600">Members: {selectedPendingBooking.member_count} | Days: {selectedPendingBooking.num_days}</p>
              </div>

              <p className="text-gray-700 mb-6">
                You have a pending booking that has not been paid yet. What would you like to do?
              </p>

              {/* Options */}
              <div className="space-y-3">
                {/* Option 1: Complete Payment */}
                <button
                  onClick={() => {
                    setShowPendingOptionsModal(false);
                    retryPayment(selectedPendingBooking);
                  }}
                  disabled={loading}
                  className="w-full bg-yellow-500 text-white py-3 rounded-lg font-semibold hover:bg-yellow-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                >
                  <ExternalLink size={18} />
                  Complete Existing Payment
                </button>

                {/* Option 2: Cancel & Book New */}
                <button
                  onClick={() => cancelPendingBooking(selectedPendingBooking, true)}
                  disabled={cancellingBooking}
                  className="w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                >
                  {cancellingBooking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <X size={18} />
                      Cancel & Start New Booking
                    </>
                  )}
                </button>

                {/* Option 3: Cancel Only (without starting new) */}
                <button
                  onClick={() => cancelPendingBooking(selectedPendingBooking, false)}
                  disabled={cancellingBooking}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                >
                  {cancellingBooking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      Cancel Booking Only
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Cancelling will remove this pending booking. You can then create a new booking with different options if needed.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationBooking;
